import type { FC } from 'react';
import { useEffect, useState } from 'react';

import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';

import Chip from './chip';
import MenuItem from './menu-item';
import Skeleton from './skeleton';

import { Caption, SubTitle1 } from '../typography';

interface IOption {
  id: number;
  name: string;
  email: string;
  checked: boolean;
  isHeading?: boolean;
  isMainHeading?: boolean;
}

const StyledSelect = styled(Select)(({ theme }) => ({
  maxWidth: 768,
  width: '100%',
  marginBottom: 16,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
  '.MuiSelect-multiple': {
    paddingTop: 11.5,
    paddingBottom: 11.5,
  },
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  '&.Mui-disabled': {
    opacity: 1,
  },
}));

type IProps = SelectProps & {
  label?: string;
  width?: string | number;
  preload?: boolean;
  options: IOption[];
  callback: (s: IOption[]) => void;
  removeFirstOnSignalUnSelect?: boolean;
  unSelectAllOnFirstSelect?: boolean;
};

const MultiSelect: FC<IProps> = ({
  id = 'mSelect',
  label,
  options,
  callback,
  className,
  defaultValue = [] as IOption[],
  width,
  preload,
  removeFirstOnSignalUnSelect = false,
  unSelectAllOnFirstSelect = false,
  ...rest
}) => {
  const [selected, setSelected] = useState<IOption[]>(defaultValue as unknown as IOption[]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (_e: SelectChangeEvent<unknown>, { props: { id: optId } }: any) => {
    let nSelected = [...selected];
    nSelected[optId] = { ...nSelected[optId], checked: !nSelected[optId].checked };

    if (optId === 0) {
      nSelected = nSelected.map((item) => ({
        ...item,
        checked: item.isHeading ? true : unSelectAllOnFirstSelect ? nSelected[optId].checked : item.checked,
      }));
    } else {
      const allSelected = nSelected.every((item) => item.id === 0 || item.checked);
      nSelected[0] = {
        ...nSelected[0],
        checked: removeFirstOnSignalUnSelect ? allSelected : nSelected[0].checked || allSelected,
      };
    }

    setSelected(() => nSelected);
    if (callback) callback(nSelected);
  };

  useEffect(() => {
    if (selected.find((item) => item.id === 0)) {
      const nSelected = options.map((item) => ({ ...item, checked: true }));
      setSelected(nSelected);
      if (callback) callback(nSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  if (preload) {
    return <Skeleton width={width} height={49} variant="rectangular" className="mb-4" />;
  }

  return (
    <>
      <InputLabel id={id}>{label}</InputLabel>
      <StyledSelect
        {...rest}
        labelId={id}
        multiple
        fullWidth
        className={`${className || ''}`}
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={(s: unknown) => {
          const options = s as IOption[];
          return (
            <div className="mr-7 flex gap-1">
              {options.map((item: IOption) => {
                return options[0].checked ? (
                  item.id === 0 ? (
                    <Chip size="small" key={selected[0].id} label={selected[0].name} />
                  ) : null
                ) : item.checked && !item.isHeading && !item.isMainHeading ? (
                  <Chip size="small" key={item.id} label={item.name} />
                ) : null;
              })}
            </div>
          );
        }}
      >
        {selected.map((o) => (
          <StyledMenuItem
            key={o.id}
            value={o.name}
            id={o.id as unknown as string}
            className="py-0"
            disabled={o.isHeading}
          >
            {!o.isHeading && <Checkbox className="p-1" checked={o.checked} />}
            {o.isMainHeading && <SubTitle1>{o.name}</SubTitle1>}
            {o.isHeading && !o.isMainHeading && <Caption className="my-1 font-bold">{o.name}</Caption>}
            {!(o.isHeading || o.isMainHeading) && <Caption className="text-text-primary">{o.name}</Caption>}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </>
  );
};

export default MultiSelect;
